import {
  Avatar,
  Body,
  Heading,
  Icon,
  IconButton,
  Tooltip,
} from '@assembly-js/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { notify } from 'src/clients/ApiService';
import { IconNameToComponent } from 'src/components/icons';
import { Link } from 'src/components/Link';
import { Client, CLIENT_DETAILS_PAGE } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { MutateClientModal } from 'src/features/crm/clients/MutateClientModal/MutateClientModal';
import { useCompanyClientAssignment } from 'src/features/crm/hooks/useCompanyClientAssignment';
import { useDataLossWarning } from 'src/features/crm/hooks/useDataLossWarning';
import { MenuAction } from 'src/features/crm/MenuAction';
import {
  CreateClientMagicLinkDialog,
  ShareTypes,
} from 'src/features/magic-links/CreateClientMagicLinkDialog';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { InviteClientModal } from 'src/legacy/components/Client/InviteClientModal';
import { ConfirmDeleteModal } from 'src/legacy/components/Modals/ConfirmDeleteModal';
import {
  SelectClientOption,
  SelectClients,
} from 'src/routes/companies/components/SelectClients';
import { useManageCompanyClientsMutation } from 'src/services/api/companiesApi';
import { deleteClient } from 'src/store/clients/actions';
import { Company } from 'src/store/clients/types';
import { formatStatus, getInitials } from 'src/utils/UserUtils';

export function CompanyDetailsClients({
  clients: unsortedClients,
  company,
}: {
  clients: Client[];
  company: Company;
}) {
  const { t } = useTranslation();
  const { authPreferences } = React.useContext(PortalConfigContext);
  const allClients = useAppSelector((state) => state.clients.activeClients);
  const enableMagicLinks = authPreferences?.enableMagicLinks;
  // Sort clients based on the following instructions:
  // 1. There are three groups that should appear in this order:
  //    - not invited - client.fields.invitedBy is truthy
  //    - invited     - the opposite of ^^
  //    - active      - client.fields.userStatus = 'CONFIRMED'
  // 2. Within those groups, sort by client.updatedDate in reverse chronological order.
  const companyClients = React.useMemo(() => {
    return unsortedClients.sort((a, b) => {
      const getGroupOrder = (client: Client) => {
        // we're relying on checking for confirmed first to avoid
        // conflicts with the invitedBy field, that will be set for
        // confirmed clients.
        if (client.fields.userStatus === 'CONFIRMED') return 2;
        if (client.fields.invitedBy) return 1;
        return 0;
      };

      const groupOrderA = getGroupOrder(a);
      const groupOrderB = getGroupOrder(b);

      if (groupOrderA !== groupOrderB) {
        return groupOrderA - groupOrderB;
      }

      return (
        new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
      );
    });
  }, [unsortedClients]);

  const dispatch = useAppDispatch();
  const [inviteClient, setInviteClient] = React.useState<Client | null>(null);
  const [deleteClientUser, setDeleteClientUser] = React.useState<Client | null>(
    null,
  );
  const [isCreatingClient, setIsCreatingClient] = React.useState(false);

  const closeDeleteClientModal = () => {
    setDeleteClientUser(null);
  };

  const handleDeleteClientConfirmed = (c: Client) => {
    dispatch(deleteClient(c));
    closeDeleteClientModal();
  };

  const [shareLinkState, setShareLinkState] = React.useState<
    ShareTypes | undefined
  >();

  const [isTruncatedMap, setIsTruncatedMap] = React.useState<
    Record<string, boolean>
  >({});

  const handleNameRef = React.useCallback(
    (clientId: string) => (node: HTMLSpanElement | null) => {
      if (node) {
        const isTruncated = node.scrollWidth > node.clientWidth;
        setIsTruncatedMap((prev) => {
          if (prev[clientId] === isTruncated) return prev;
          return { ...prev, [clientId]: isTruncated };
        });
      }
    },
    [],
  );

  const [manageCompanyClients] = useManageCompanyClientsMutation();
  const handleUndoRemoveClient = async (clientId: string) => {
    await manageCompanyClients({
      companyId: company.id,
      addClientIds: [clientId],
      removeClientIds: [],
    });

    notify({
      status: 'success',
      successMessage: 'Client restored to this company.',
      dispatch,
    });
  };
  const { removeClientWithWarning, DataLossWarningModal } = useDataLossWarning({
    onSuccess: (params) => {
      notify({
        status: 'success',
        successMessage: 'Client removed from this company.',
        dispatch,
        actionProps: {
          children: 'Undo',
          onClick: () => {
            handleUndoRemoveClient(params.clientId);
          },
        },
      });
    },
  });

  const handleRemoveClient = (clientId: string) => {
    const client = allClients.find((c) => c.id === clientId);
    const isLastCompany = (client?.fields?.companyIds?.length ?? 0) <= 1;

    removeClientWithWarning({
      companyId: company.id,
      clientId,
      companyName: company.fields.name,
      isLastCompany,
    });
  };

  // Use useResizeObserver for sidebar width
  const { ref: sidebarRef, rect } = useResizeObserver<HTMLDivElement>();
  const [initialClientData, setInitialClientData] = React.useState<{
    cognitoFirstName?: string;
    cognitoLastName?: string;
  }>({});
  const {
    assignClientToCompany,
    PlaceholderPromotionModal,
    PlaceholderDeletionModal,
  } = useCompanyClientAssignment();
  const handleClientSelection = async (option: SelectClientOption | null) => {
    // when user clicks on an existing client option
    // assign the client to the company. This would be skiped
    // if the user clicks on the "Create {option.label} as new client" option,
    // so this is what this check is for.
    if (!option?.client) {
      setIsCreatingClient(true);
      const [firstName, lastName] = option?.label?.split(' ') ?? [];
      setInitialClientData({
        cognitoFirstName: firstName,
        cognitoLastName: lastName,
      });
      return;
    }
    await assignClientToCompany({ company, client: option.client });
  };

  const unassignedClients = allClients.filter(
    (c) => !companyClients.map((client) => client.id).includes(c.id),
  );
  const [showSelectClients, setShowSelectClients] = React.useState(false);
  const handleAddClient = () => {
    setShowSelectClients(true);
  };
  return (
    <>
      <PlaceholderPromotionModal />
      <PlaceholderDeletionModal />
      <DataLossWarningModal />
      <div className="h-full w-full pb-6">
        <header className="px-4 flex flex-col">
          <div className="flex flex-row justify-between">
            <Heading>
              {t(($) => $.client, {
                count: companyClients.length,
              })}
            </Heading>
            <IconButton
              className="p-1"
              label="Add client"
              onClick={handleAddClient}
              icon="Plus"
              variant="minimal"
            />
          </div>
          {showSelectClients && (
            <SelectClients
              clients={unassignedClients}
              onChange={(option) => {
                handleClientSelection(option);
                setShowSelectClients(false);
              }}
              onBlur={() => setShowSelectClients(false)}
            />
          )}
        </header>

        {companyClients.length === 0 ? (
          <div className="w-full flex justify-center items-center">
            <Body className="text-secondary">
              {t(($) => $.messages.click_plus_to_add_client)}
            </Body>
          </div>
        ) : (
          <div ref={sidebarRef} className="w-full pt-3">
            {companyClients.map((client) => {
              const actions = [];

              if (enableMagicLinks) {
                actions.push({
                  // TODO: update icon name once we upgrade the design system library
                  icon: (
                    <IconNameToComponent.PaperPlaneIcon className="w-[14px] h-[14px]" />
                  ),
                  name: 'Email magic link',
                  onClick: () => {
                    setInviteClient(client);
                    setShareLinkState({ email: true, link: false });
                  },
                });
                actions.push({
                  icon: <Icon className="h-[14px] w-[14px]" icon="Copy" />,
                  name: 'Copy magic link',
                  onClick: () => {
                    setInviteClient(client);
                    setShareLinkState({ email: false, link: true });
                  },
                });
              } else {
                if (
                  !client.fields.invitedBy &&
                  client.fields.userStatus !== 'CONFIRMED'
                ) {
                  actions.push({
                    icon: (
                      // TODO: Use the design system component here, it renders
                      //       with a dark fill, rather than just an outline right now.
                      <IconNameToComponent.PaperPlaneIcon className="w-[14px] h-[14px]" />
                    ),
                    name: t(($) => $.actions.invite_client),
                    onClick: () => {
                      setInviteClient(client);
                    },
                  });
                }
              }
              actions.push({
                icon: <Icon icon="Close" />,
                isDelete: true,
                name: 'Remove client',
                onClick: () => {
                  handleRemoveClient(client.id);
                },
              });

              return (
                <div
                  key={client.id}
                  className="flex flex-row items-center py-1 gap-3 hover:bg-secondary-hover px-4 cursor-pointer"
                >
                  <Link
                    href={`${CLIENT_DETAILS_PAGE.path}/${client.id}`}
                    onMouseDown={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        window.open(
                          `${CLIENT_DETAILS_PAGE.path}/${client.id}`,
                          '_blank',
                        );
                        e.preventDefault();
                      }
                    }}
                    className="flex justify-between items-center gap-2 flex-1 "
                  >
                    <div className="flex gap-2 items-center ">
                      <Avatar
                        src={client.fields.avatarImageUrl ?? ''}
                        fallbackColor={client.fields.fallbackColor}
                        text={getInitials(
                          `${client.fields.givenName} ${client.fields.familyName}`,
                          {
                            twoLetters: true,
                          },
                        )}
                        size="sm"
                        variant="circle"
                        alt={`${client.fields.givenName} ${client.fields.familyName}`}
                      />

                      <Tooltip
                        content={
                          <Body size="sm">
                            {`${client.fields.givenName} ${client.fields.familyName}`}
                          </Body>
                        }
                        position="top"
                        disabled={!isTruncatedMap[client.id]}
                      >
                        <div
                          className="hover:no-underline flex flex-col"
                          //  The maximum width is dynamically calculated based on the ResizeSidebar
                          style={
                            rect.width
                              ? {
                                  maxWidth: Math.max(60, rect.width - 180),
                                }
                              : undefined
                          }
                        >
                          <span
                            className="text-sm font-medium truncate my-0"
                            ref={handleNameRef(client.id)}
                          >
                            {`${client.fields.givenName} ${client.fields.familyName}`}
                          </span>
                        </div>
                      </Tooltip>
                    </div>
                    <Body className="text-right flex-1" size="sm">
                      {formatStatus(client)}
                    </Body>
                  </Link>

                  <MenuAction actions={actions} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {inviteClient && !enableMagicLinks && (
        <InviteClientModal
          clients={[inviteClient]}
          onClose={() => setInviteClient(null)}
          open={true}
        />
      )}
      {inviteClient && enableMagicLinks && shareLinkState && (
        <CreateClientMagicLinkDialog
          open={true}
          clients={[inviteClient]}
          onCancel={() => setShareLinkState(undefined)}
          onSuccess={() => setShareLinkState(undefined)}
          shareTypes={shareLinkState}
        />
      )}
      {deleteClientUser && (
        <ConfirmDeleteModal
          type="client"
          descriptionMeta={{
            givenName: deleteClientUser.fields.givenName,
            familyName: deleteClientUser.fields.familyName,
          }}
          onConfirmDelete={() => handleDeleteClientConfirmed(deleteClientUser)}
          onClose={closeDeleteClientModal}
          open={true}
        />
      )}
      <MutateClientModal
        company={company}
        initialClientData={initialClientData}
        open={isCreatingClient}
        onCancel={() => {
          setIsCreatingClient(false);
        }}
        onClientCreated={(client) => {
          setInviteClient(client);
          setShareLinkState({ email: true, link: true });
          setIsCreatingClient(false);
        }}
      />
    </>
  );
}
